import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bot,
  ChevronDown,
  Clock3,
  HelpCircle,
  MessageCircle,
  Plus,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import API from "../services/api";
import "./LotsuChatbot.css";

const CHAT_STORAGE_KEY = "lotsu-chat-sessions";
const CHAT_TTL_MS = 3 * 60 * 60 * 1000;

const languages = [
  { code: "hi", label: "Hindi", shortLabel: "HI" },
  { code: "en", label: "English", shortLabel: "EN" },
  { code: "hinglish", label: "Hinglish", shortLabel: "HIN" },
];

const languageContent = {
  hi: {
    askLanguage: "Namaste, main Lotsu hoon. Kripya pehle apni language choose karein.",
    chosen: "Hindi select ho gayi. Ab aap apna sawal pooch sakte hain.",
    subtitle: "Software sahayak",
    suggestionsLabel: "Suggested questions",
    helperSuggested: "Suggested help ready",
    helperInstant: "Instant replies",
    placeholder: "Apna sawal likhein...",
    quickQuestions: [
      "Attendance kaise mark karu?",
      "Vendor requirement kahan milegi?",
      "Salary/payment status kaise dekhu?",
      "Report download kaise hogi?",
      "Password bhool gaya hoon",
      "Admin se help chahiye",
    ],
    answers: {
      attendance:
        "Attendance ke liye apne role ke dashboard me Attendance section open karein. Location/photo permission allow karke check-in ya check-out submit karein. Agar button disabled ho to internet, location permission aur login session check karein.",
      vendorRegister:
        "Vendor ke roop me register karne ke liye login page par 'Vendor Register' ya 'Register as Vendor' option par click karein. Form me company/person details, mobile number, address aur required documents bhar kar submit karein. Submit hone ke baad admin approval ke baad aap login kar paayenge.",
      supplierRegister:
        "Supplier register karne ke liye login page par 'Supplier Register' option par click karein. Supplier details, contact number, address aur required documents bhar kar form submit karein. Admin approval ke baad supplier login active hoga.",
      employeeRegister:
        "Employee registration ke liye login page par 'Employee Register' option use karein. Personal details, mobile number aur required information submit karein. Approval/activation ke baad employee panel access milega.",
      contractorRegister:
        "Contractor registration ke liye login page par 'Contractor Register' option click karein. Contractor details aur work/payment related information bhar kar submit karein. Approval ke baad contractor account use kar sakte hain.",
      vendor:
        "Vendor requirement dekhne ke liye Vendor Dashboard me Requirements ya Assigned Requirements section open karein. Wahan quantity, status, dispatch aur history details milengi.",
      supplier:
        "Supplier ke kaam ke liye Supplier Dashboard me PO, Dispatch, Certificate aur Quality related sections use karein. Pending item par click karke detail view me next action milta hai.",
      payment:
        "Payment ya salary status ke liye Payment History, Salary, Payout Desk ya PhonePe Transactions page check karein. Agar status pending dikhe to thoda wait karke refresh karein; failed status par admin/accounts team se contact karein.",
      report:
        "Reports page me date range aur filters select karein, phir Generate/Download/Print action use karein. Empty report aaye to selected dates aur role permissions verify karein.",
      login:
        "Login issue me pehle mobile/email aur role check karein. Password bhool gaye hain to Forgot Password option use karein. OTP na aaye to network check karke resend karein.",
      employee:
        "Employee details ke liye Profile, ID Card, Agreement aur Documents sections open karein. Koi detail galat ho to admin/HR se update request karein.",
      admin:
        "Admin access ya permission ke liye Admin Dashboard me Departments/Roles, Employee Detail ya Admin Profile related settings check karein. Agar menu nahi dikh raha hai to aapke role me permission missing ho sakti hai.",
      guard:
        "Guard workflow ke liye Guard Dashboard me Attendance, Vehicle Lookup aur Logs sections milenge. Vehicle number enter karke entry status aur gate log update kar sakte hain.",
    },
    fallbacks: [
      "Is sawal ke liye mujhe thoda aur detail chahiye. Aap page ka naam, error message ya jo action karna hai woh likh dein.",
      "Main guide kar sakta hoon. Kripya batayein ki aap admin, employee, vendor, supplier, contractor ya guard panel me hain?",
      "Agar screen par error aa raha hai to exact message bhej dein. Tab main next step clearly bata dunga.",
    ],
  },
  en: {
    askLanguage: "Hi, I am Lotsu. Please choose your language first.",
    chosen: "English selected. You can ask your question now.",
    subtitle: "Software help assistant",
    suggestionsLabel: "Suggested questions",
    helperSuggested: "Suggested help ready",
    helperInstant: "Instant replies",
    placeholder: "Type your question...",
    quickQuestions: [
      "How do I mark attendance?",
      "Where can I find vendor requirements?",
      "How do I check salary/payment status?",
      "How do I download a report?",
      "I forgot my password",
      "I need admin help",
    ],
    answers: {
      attendance:
        "Open the Attendance section from your role dashboard. Allow location/photo permission, then submit check-in or check-out. If the button is disabled, check internet, location permission and your login session.",
      vendorRegister:
        "To register yourself as a vendor, open the login page and click 'Vendor Register' or 'Register as Vendor'. Fill in your business/person details, mobile number, address and required documents, then submit the form. After admin approval, you will be able to log in as a vendor.",
      supplierRegister:
        "To register as a supplier, open the login page and click 'Supplier Register'. Fill in supplier details, contact number, address and required documents, then submit the form. After admin approval, your supplier login will be active.",
      employeeRegister:
        "To register as an employee, use the 'Employee Register' option on the login page. Submit personal details, mobile number and required information. After approval or activation, you can access the employee panel.",
      contractorRegister:
        "To register as a contractor, click 'Contractor Register' on the login page. Fill in contractor details and work/payment related information, then submit. After approval, you can use the contractor account.",
      vendor:
        "Open Requirements or Assigned Requirements from the Vendor Dashboard. You will find quantity, status, dispatch and history details there.",
      supplier:
        "For supplier work, use the PO, Dispatch, Certificate and Quality sections in the Supplier Dashboard. Open a pending item to see the next action.",
      payment:
        "For payment or salary status, check Payment History, Salary, Payout Desk or PhonePe Transactions. If the status is pending, wait and refresh. For failed status, contact admin/accounts.",
      report:
        "Open the Reports page, select date range and filters, then use Generate, Download or Print. If the report is empty, verify selected dates and role permissions.",
      login:
        "For login issues, check mobile/email and role first. If you forgot the password, use Forgot Password. If OTP does not arrive, check network and use resend.",
      employee:
        "For employee details, open Profile, ID Card, Agreement or Documents. If any detail is wrong, request an update from admin/HR.",
      admin:
        "For admin access or permissions, check Departments/Roles, Employee Detail or Admin Profile settings. If a menu is missing, your role may not have that permission.",
      guard:
        "For guard workflow, use Attendance, Vehicle Lookup and Logs from the Guard Dashboard. Enter vehicle number to update entry status and gate logs.",
    },
    fallbacks: [
      "I need a little more detail for this. Please share the page name, error message or action you want to perform.",
      "I can guide you. Please tell me whether you are using admin, employee, vendor, supplier, contractor or guard panel.",
      "If an error is showing on screen, send the exact message. I will tell you the next step clearly.",
    ],
  },
  hinglish: {
    askLanguage: "Hi, main Lotsu hoon. Pehle language choose kar lo.",
    chosen: "Hinglish selected. Ab apna question pooch lo.",
    subtitle: "Software help assistant",
    suggestionsLabel: "Suggested questions",
    helperSuggested: "Suggested help ready",
    helperInstant: "Instant replies",
    placeholder: "Apna question type karo...",
    quickQuestions: [
      "Attendance kaise mark karu?",
      "Vendor requirement kahan milegi?",
      "Payment status kaise check karu?",
      "Report download kaise hogi?",
      "Password forgot ho gaya",
      "Admin help chahiye",
    ],
    answers: {
      attendance:
        "Attendance mark karne ke liye apne dashboard me Attendance section open karo. Location/photo permission allow karo, phir check-in ya check-out submit karo.",
      vendorRegister:
        "Vendor register karne ke liye login page par 'Vendor Register' ya 'Register as Vendor' option click karo. Business/person details, mobile number, address aur required documents fill karke submit karo. Admin approval ke baad vendor login ho jayega.",
      supplierRegister:
        "Supplier register karne ke liye login page par 'Supplier Register' option click karo. Supplier details, contact number, address aur required documents fill karke submit karo. Admin approval ke baad supplier login active ho jayega.",
      employeeRegister:
        "Employee register karne ke liye login page par 'Employee Register' option use karo. Personal details, mobile number aur required information submit karo. Approval ke baad employee panel access milega.",
      contractorRegister:
        "Contractor register karne ke liye login page par 'Contractor Register' option click karo. Contractor details aur work/payment information fill karke submit karo. Approval ke baad contractor account use kar sakte ho.",
      vendor:
        "Vendor requirement ke liye Vendor Dashboard me Requirements ya Assigned Requirements open karo. Wahan quantity, status, dispatch aur history milegi.",
      supplier:
        "Supplier work ke liye Supplier Dashboard me PO, Dispatch, Certificate aur Quality sections use karo. Pending item open karke next action dekh sakte ho.",
      payment:
        "Salary/payment status ke liye Payment History, Salary, Payout Desk ya PhonePe Transactions check karo. Pending ho to refresh karo; failed ho to admin/accounts ko bolo.",
      report:
        "Report download ke liye Reports page me date range aur filters select karo, phir Generate/Download/Print use karo.",
      login:
        "Login issue me mobile/email aur role check karo. Password forgot ho gaya ho to Forgot Password use karo. OTP na aaye to resend karo.",
      employee:
        "Employee details ke liye Profile, ID Card, Agreement aur Documents section open karo. Galat detail ho to admin/HR se update karwao.",
      admin:
        "Admin permission ke liye Departments/Roles, Employee Detail ya Admin Profile settings check karo. Menu missing hai to permission missing ho sakti hai.",
      guard:
        "Guard workflow me Attendance, Vehicle Lookup aur Logs use karo. Vehicle number enter karke gate entry status update kar sakte ho.",
    },
    fallbacks: [
      "Thoda aur detail bhejo. Page name, error message ya jo kaam karna hai woh likh do.",
      "Main guide kar dunga. Batao aap admin, employee, vendor, supplier, contractor ya guard panel me ho?",
      "Screen par error aa raha hai to exact message bhejo, main next step bata dunga.",
    ],
  },
};

const answerTopics = [
  {
    key: "vendorRegister",
    roleKeywords: ["vendor"],
    actionKeywords: ["register", "registration", "signup", "sign up", "create account", "account bana", "my self", "myself"],
  },
  {
    key: "supplierRegister",
    roleKeywords: ["supplier"],
    actionKeywords: ["register", "registration", "signup", "sign up", "create account", "account bana", "my self", "myself"],
  },
  {
    key: "employeeRegister",
    roleKeywords: ["employee", "staff", "worker"],
    actionKeywords: ["register", "registration", "signup", "sign up", "create account", "account bana", "my self", "myself"],
  },
  {
    key: "contractorRegister",
    roleKeywords: ["contractor"],
    actionKeywords: ["register", "registration", "signup", "sign up", "create account", "account bana", "my self", "myself"],
  },
  { key: "attendance", actionKeywords: ["attendance", "hazri", "present", "check in", "check-in", "check out", "check-out", "mark"] },
  { key: "vendor", roleKeywords: ["vendor"], actionKeywords: ["requirement", "requirements", "demand", "dispatch", "assigned"] },
  { key: "supplier", roleKeywords: ["supplier"], actionKeywords: ["po", "purchase order", "quality", "certificate", "dispatch", "pending"] },
  { key: "payment", actionKeywords: ["salary", "payment", "payout", "phonepe", "transaction", "pay", "paid", "status"] },
  { key: "report", actionKeywords: ["report", "download", "pdf", "excel", "print"] },
  { key: "login", actionKeywords: ["login", "password", "forgot", "otp", "reset", "signin", "sign in", "cannot login", "can't login"] },
  { key: "employee", roleKeywords: ["employee"], actionKeywords: ["id card", "profile", "document", "agreement", "details"] },
  { key: "admin", roleKeywords: ["admin"], actionKeywords: ["permission", "access", "role", "department", "menu"] },
  { key: "guard", roleKeywords: ["guard", "security"], actionKeywords: ["vehicle", "gate", "attendance", "logs", "entry"] },
];

const isSessionExpired = (session) =>
  Date.now() - (session?.updatedAt || session?.createdAt || 0) > CHAT_TTL_MS;

const formatSessionTime = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const loadStoredSessions = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CHAT_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const saveStoredSessions = (sessions) => {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(sessions.slice(0, 12)));
  } catch (_error) {
    // Storage can fail in private mode; chat still works for the current page.
  }
};

const countMatches = (message, keywords = []) =>
  keywords.reduce((score, keyword) => {
    if (!message.includes(keyword)) return score;
    return score + (keyword.includes(" ") ? 2 : 1);
  }, 0);

const getBotAnswer = (message, language) => {
  const normalized = message.toLowerCase();
  const content = languageContent[language] || languageContent.hi;
  const scoredMatches = answerTopics
    .map((item, index) => {
      const roleScore = countMatches(normalized, item.roleKeywords);
      const actionScore = countMatches(normalized, item.actionKeywords);
      const hasRoleRequirement = Array.isArray(item.roleKeywords) && item.roleKeywords.length > 0;
      const hasActionRequirement =
        Array.isArray(item.actionKeywords) && item.actionKeywords.length > 0;

      if (hasRoleRequirement && roleScore === 0) return null;
      if (hasActionRequirement && actionScore === 0) return null;

      return {
        item,
        index,
        score: roleScore + actionScore * 2,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || a.index - b.index);
  const match = scoredMatches[0]?.score >= 2 ? scoredMatches[0].item : null;

  if (match) return content.answers[match.key];

  const index = Math.abs(
    normalized.split("").reduce((total, char) => total + char.charCodeAt(0), 0)
  ) % content.fallbacks.length;

  return content.fallbacks[index];
};

function LotsuChatbot() {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const activeContent = languageContent[language] || languageContent.hi;

  const visibleSuggestions = useMemo(
    () => activeContent.quickQuestions.slice(0, 6),
    [activeContent]
  );
  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId),
    [sessions, activeSessionId]
  );

  useEffect(() => {
    setSessions(loadStoredSessions());
  }, []);

  useEffect(() => {
    saveStoredSessions(sessions);
  }, [sessions]);

  useEffect(() => {
    if (open && language) {
      window.setTimeout(() => inputRef.current?.focus(), 160);
    }
  }, [open, language]);

  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, typing, language]);

  const chooseLanguage = (nextLanguage) => {
    const content = languageContent[nextLanguage] || languageContent.hi;
    const now = Date.now();
    const session = {
      id: `lotsu-${now}`,
      language: nextLanguage,
      title: "New chat",
      createdAt: now,
      updatedAt: now,
      messages: [
        {
          id: `language-${now}`,
          from: "bot",
          text: content.chosen,
          createdAt: now,
        },
      ],
    };

    setLanguage(nextLanguage);
    setMessages(session.messages);
    setActiveSessionId(session.id);
    setShowHistory(false);
    setSessions((current) => [session, ...current].slice(0, 12));
  };

  const startNewChat = () => {
    setLanguage("");
    setMessages([]);
    setActiveSessionId("");
    setInput("");
    setTyping(false);
    setShowHistory(false);
  };

  const openOldChat = (session) => {
    setLanguage(session.language);
    setMessages(session.messages || []);
    setActiveSessionId(session.id);
    setShowHistory(false);
    setInput("");
    setTyping(false);
  };

  const sendMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing || !language) return;
    const now = Date.now();
    const expired = activeSession && isSessionExpired(activeSession);
    const sessionId = expired || !activeSessionId ? `lotsu-${now}` : activeSessionId;
    const baseMessages = expired
      ? [
          {
            id: `expired-${now}`,
            from: "bot",
            text:
              language === "en"
                ? "The previous chat is older than 3 hours, so I have started a new chat."
                : language === "hinglish"
                  ? "Purani chat 3 ghante se old ho gayi thi, isliye maine nayi chat start kar di."
                  : "Purani chat 3 ghante se zyada purani ho gayi thi, isliye maine nayi chat shuru kar di.",
            createdAt: now,
          },
        ]
      : messages;

    const userMessage = {
      id: `user-${now}`,
      from: "user",
      text: trimmed,
      createdAt: now,
    };
    const nextMessages = [...baseMessages, userMessage];

    setMessages(nextMessages);
    setActiveSessionId(sessionId);
    setInput("");
    setTyping(true);
    setSessions((current) => {
      const withoutCurrent = current.filter((session) => session.id !== sessionId);
      const existing = current.find((session) => session.id === sessionId);
      const nextSession = {
        id: sessionId,
        language,
        title:
          existing?.title && existing.title !== "New chat"
            ? existing.title
            : trimmed.slice(0, 42),
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        messages: nextMessages,
      };
      return [nextSession, ...withoutCurrent].slice(0, 12);
    });

    const requestHistory = nextMessages
      .filter((messageItem) => messageItem.from === "user" || messageItem.from === "bot")
      .slice(-10);

    window.setTimeout(async () => {
      let reply = "";
      try {
        const response = await API.post("/chatbot/message", {
          message: trimmed,
          language,
          history: requestHistory,
        });
        reply = response.data?.reply || "";
      } catch (_error) {
        reply = "";
      }

      const botMessage = {
          id: `bot-${Date.now()}`,
          from: "bot",
          text: reply || getBotAnswer(trimmed, language),
          createdAt: Date.now(),
        };
      setMessages((current) => {
        const updatedMessages = [...current, botMessage];
        setSessions((currentSessions) =>
          currentSessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  updatedAt: Date.now(),
                  messages: updatedMessages,
                  title: session.title === "New chat" ? trimmed.slice(0, 42) : session.title,
                }
              : session
          )
        );
        return updatedMessages;
      });
      setTyping(false);
    }, 520);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <div className={`lotsu-chatbot ${open ? "is-open" : ""}`}>
      {open && (
        <section className="lotsu-panel" aria-label="Lotsu chatbot">
          <header className="lotsu-header">
            <div className="lotsu-avatar" aria-hidden="true">
              <span className="lotsu-grain lotsu-grain-large" />
              <Bot size={19} strokeWidth={2.3} />
            </div>
            <div className="lotsu-title-block">
              <div className="lotsu-title-row">
                <h2>Lotsu</h2>
                <span className="lotsu-status">
                  <span />
                  {language ? languages.find((item) => item.code === language)?.shortLabel : "Online"}
                </span>
              </div>
              <p>{activeContent.subtitle}</p>
            </div>
            <button
              type="button"
              className="lotsu-icon-button"
              aria-label="Start new Lotsu chat"
              onClick={startNewChat}
            >
              <Plus size={19} />
            </button>
            <button
              type="button"
              className="lotsu-icon-button"
              aria-label="Close Lotsu chat"
              onClick={() => setOpen(false)}
            >
              <ChevronDown size={20} />
            </button>
          </header>

          <div className="lotsu-chat-actions">
            <button type="button" onClick={startNewChat}>
              <Plus size={14} />
              New chat
            </button>
            <button type="button" onClick={() => setShowHistory((current) => !current)}>
              <Clock3 size={14} />
              Old chats
            </button>
          </div>

          {showHistory && (
            <div className="lotsu-history-panel">
              {sessions.length === 0 ? (
                <div className="lotsu-history-empty">No old chats yet</div>
              ) : (
                sessions.map((session) => {
                  const expired = isSessionExpired(session);
                  return (
                    <button
                      type="button"
                      key={session.id}
                      className={`${session.id === activeSessionId ? "is-active" : ""} ${
                        expired ? "is-expired" : ""
                      }`}
                      onClick={() => openOldChat(session)}
                    >
                      <span>
                        <strong>{session.title || "Old chat"}</strong>
                        <small>
                          {languages.find((item) => item.code === session.language)?.label || "Chat"}
                          {" . "}
                          {formatSessionTime(session.updatedAt)}
                        </small>
                      </span>
                      <em>{expired ? "Closed" : "Active"}</em>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {language && (
            <div className="lotsu-suggestion-strip" aria-label={activeContent.suggestionsLabel}>
              {visibleSuggestions.map((question) => (
                <button
                  type="button"
                  key={question}
                  onClick={() => sendMessage(question)}
                  disabled={typing}
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          <div className="lotsu-messages" ref={messagesRef}>
            {!language && (
              <div className="lotsu-language-card">
                <div className="lotsu-language-heading">
                  <Sparkles size={16} />
                  <span>{languageContent.hi.askLanguage}</span>
                </div>
                <div className="lotsu-language-options">
                  {languages.map((item) => (
                    <button
                      type="button"
                      key={item.code}
                      onClick={() => chooseLanguage(item.code)}
                    >
                      <strong>{item.label}</strong>
                      <small>{item.shortLabel}</small>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`lotsu-message-row ${
                  message.from === "user" ? "from-user" : "from-bot"
                }`}
              >
                {message.from === "bot" && (
                  <div className="lotsu-mini-avatar" aria-hidden="true">
                    <span className="lotsu-grain" />
                  </div>
                )}
                <div className="lotsu-bubble">{message.text}</div>
              </div>
            ))}

            {typing && (
              <div className="lotsu-message-row from-bot">
                <div className="lotsu-mini-avatar" aria-hidden="true">
                  <span className="lotsu-grain" />
                </div>
                <div className="lotsu-bubble lotsu-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          <div className="lotsu-helper-row">
            <span>
              <Sparkles size={14} />
              {activeSession && isSessionExpired(activeSession)
                ? "Old chat closed, next message starts new chat"
                : language
                  ? activeContent.helperSuggested
                  : "Choose language first"}
            </span>
            <span>
              <Clock3 size={14} />
              {activeContent.helperInstant}
            </span>
          </div>

          <form className="lotsu-input-row" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={language ? activeContent.placeholder : "Choose language first..."}
              aria-label="Message Lotsu"
              disabled={!language}
            />
            <button type="submit" disabled={!input.trim() || typing || !language} aria-label="Send message">
              <Send size={18} />
            </button>
          </form>
        </section>
      )}

      {!open && (
        <button
          type="button"
          className="lotsu-launcher"
          aria-label="Open Lotsu chat"
          onClick={() => setOpen(true)}
        >
          <span className="lotsu-launcher-orbit" aria-hidden="true">
            <span className="lotsu-grain lotsu-grain-button" />
          </span>
          <span className="lotsu-launcher-text">
            <strong>Lotsu</strong>
            <small>Ask help</small>
          </span>
          <MessageCircle size={20} />
        </button>
      )}

      {open && (
        <button
          type="button"
          className="lotsu-close-chip"
          aria-label="Close Lotsu chat"
          onClick={() => setOpen(false)}
        >
          <X size={16} />
        </button>
      )}

      {!open && (
        <div className="lotsu-nudge" aria-hidden="true">
          <HelpCircle size={14} />
          Need help?
        </div>
      )}
    </div>
  );
}

export default LotsuChatbot;
